import { darken, ThemeOptions } from "@mui/material";
import { blue, green, grey, orange, red } from "@mui/material/colors";
import React from "react";

import { NonMuiColors, fontFamilyConstants } from "@constants/theme.constants";

/**
 * @Module_Augmentation for the theme Styles
 * Extended from the sage-network shared theme
 */

declare module "@mui/material/styles" {
  interface Theme {
    status: {
      danger: string;
    };
  }
  interface TypeBackground {
    light: string;
    primary: string;
    secondary: string;
    dark: string;
  }
  interface ThemeOptions {
    status?: {
      danger?: string;
    };
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    h1Bold: React.CSSProperties;
    h1Medium: React.CSSProperties;
    h2Bold: React.CSSProperties;
    h2Medium: React.CSSProperties;
    h3Bold: React.CSSProperties;
    h3Medium: React.CSSProperties;
    h4Bold: React.CSSProperties;
    h4Medium: React.CSSProperties;
    bodyText: React.CSSProperties;
    bodyTextMedium: React.CSSProperties;
    bodyTextBold: React.CSSProperties;
    smallBody: React.CSSProperties;
    smallBodyMedium: React.CSSProperties;
    smallBodyBold: React.CSSProperties;
    captionMedium: React.CSSProperties;
    captionBold: React.CSSProperties;
    extraSmallBody: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    h1Bold?: React.CSSProperties;
    h1Medium?: React.CSSProperties;
    h2Bold?: React.CSSProperties;
    h2Medium?: React.CSSProperties;
    h3Bold?: React.CSSProperties;
    h3Medium?: React.CSSProperties;
    h4Bold?: React.CSSProperties;
    h4Medium?: React.CSSProperties;
    bodyText?: React.CSSProperties;
    bodyTextMedium?: React.CSSProperties;
    bodyTextBold?: React.CSSProperties;
    smallBody?: React.CSSProperties;
    smallBodyMedium?: React.CSSProperties;
    smallBodyBold?: React.CSSProperties;
    captionMedium?: React.CSSProperties;
    captionBold?: React.CSSProperties;
    extraSmallBody?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    h1: true;
    h1Medium: true;
    h1Bold: true;
    h2: true;
    h2Medium: true;
    h2Bold: true;
    h3: true;
    h3Medium: true;
    h3Bold: true;
    h4: true;
    h4Bold: true;
    h4Medium: true;
    h5: false;
    h6: false;
    caption: true;
    captionMedium: true;
    captionBold: true;
    subtitle1: false;
    subtitle2: false;
    overline: false;
    bodyText: true;
    bodyTextMedium: true;
    bodyTextBold: true;
    smallBody: true;
    smallBodyMedium: true;
    smallBodyBold: true;
    extraSmallBody: true;
  }
}

export const pxToRem = (px: number) => `${px / 16}rem`;

export const pxToVh = (px: number, referenceViewportHeight = 1024) =>
  `${100 * (px / referenceViewportHeight)}vh`;

export const pxToVw = (px: number, referenceViewportWidth = 1440) =>
  `${100 * (px / referenceViewportWidth)}vw`;

const StyleConstants = {
  IconHorizontalSpacing: 8,
  IconWidth: 24,
  IconHeight: 24
};

/**
 * Base theme config - mirrors sage-network's shared themeConfig
 * Extended for e-invoicing specific overrides
 */
export const themeConfig: ThemeOptions = {
  palette: {
    primary: {
      main: NonMuiColors.SAGE_GREEN
    },
    secondary: {
      main: NonMuiColors.WHITE
    },
    error: {
      main: red[900]
    },
    warning: {
      main: orange[800]
    },
    info: {
      main: blue[700]
    },
    success: {
      main: green[800]
    },
    background: {
      default: "#FAFAFA",
      paper: NonMuiColors.WHITE
    }
  },
  typography: {
    fontFamily: fontFamilyConstants.SAGE_UI_ADAPTIVE,
    allVariants: {
      margin: 0
    },
    h1: {
      fontSize: pxToRem(40),
      fontStyle: "normal",
      lineHeight: pxToRem(60),
      fontWeight: 400
    },
    h1Medium: {
      fontSize: pxToRem(40),
      fontStyle: "normal",
      lineHeight: pxToRem(60),
      fontWeight: 500
    },
    h1Bold: {
      fontSize: pxToRem(40),
      fontStyle: "normal",
      lineHeight: pxToRem(60),
      fontWeight: 600
    },
    h2: {
      fontSize: pxToRem(24),
      fontStyle: "normal",
      lineHeight: pxToRem(36),
      fontWeight: 400
    },
    h2Medium: {
      fontSize: pxToRem(24),
      fontStyle: "normal",
      lineHeight: pxToRem(36),
      fontWeight: 500
    },
    h2Bold: {
      fontSize: pxToRem(24),
      fontStyle: "normal",
      lineHeight: pxToRem(36),
      fontWeight: 600
    },
    h3: {
      fontSize: pxToRem(18),
      fontStyle: "normal",
      lineHeight: pxToRem(27),
      fontWeight: 400
    },
    h3Medium: {
      fontSize: pxToRem(18),
      fontStyle: "normal",
      lineHeight: pxToRem(27),
      fontWeight: 500
    },
    h3Bold: {
      fontSize: pxToRem(18),
      fontStyle: "normal",
      lineHeight: pxToRem(27),
      fontWeight: 600
    },
    h4: {
      fontSize: pxToRem(16),
      fontStyle: "normal",
      lineHeight: pxToRem(24),
      fontWeight: 400
    },
    h4Medium: {
      fontSize: pxToRem(16),
      fontStyle: "normal",
      lineHeight: pxToRem(24),
      fontWeight: 500
    },
    h4Bold: {
      fontSize: pxToRem(16),
      fontStyle: "normal",
      lineHeight: pxToRem(24),
      fontWeight: 600
    },
    bodyText: {
      fontSize: pxToRem(14),
      fontStyle: "normal",
      lineHeight: pxToRem(21),
      fontWeight: 400
    },
    bodyTextMedium: {
      fontSize: pxToRem(14),
      fontStyle: "normal",
      lineHeight: pxToRem(21),
      fontWeight: 500
    },
    bodyTextBold: {
      fontSize: pxToRem(14),
      fontStyle: "normal",
      lineHeight: pxToRem(21),
      fontWeight: 600
    },
    smallBody: {
      fontSize: pxToRem(12),
      fontStyle: "normal",
      lineHeight: pxToRem(18),
      fontWeight: 400
    },
    smallBodyMedium: {
      fontSize: pxToRem(12),
      fontStyle: "normal",
      lineHeight: pxToRem(18),
      fontWeight: 500
    },
    smallBodyBold: {
      fontSize: pxToRem(12),
      fontStyle: "normal",
      lineHeight: pxToRem(18),
      fontWeight: 600
    },
    caption: {
      fontSize: pxToRem(10),
      fontStyle: "normal",
      lineHeight: pxToRem(12.5),
      fontWeight: 400
    },
    captionMedium: {
      fontSize: pxToRem(10),
      fontStyle: "normal",
      lineHeight: pxToRem(12.5),
      fontWeight: 500
    },
    captionBold: {
      fontSize: pxToRem(10),
      fontStyle: "normal",
      lineHeight: pxToRem(12.5),
      fontWeight: 600
    },
    extraSmallBody: {
      fontSize: pxToRem(10),
      fontStyle: "normal",
      lineHeight: pxToRem(15),
      fontWeight: 400
    }
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: fontFamilyConstants.SAGE_UI_ADAPTIVE
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
        disableFocusRipple: true,
        size: "large"
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: pxToRem(32),
          fontWeight: 500,
          fontSize: pxToRem(12),
          padding: `${pxToRem(8)} ${pxToRem(16)}`,
          transition: "background 300ms linear"
        },
        containedSizeMedium: {
          height: pxToRem(32)
        },
        containedSizeLarge: {
          height: pxToRem(40),
          fontSize: pxToRem(14)
        },
        textSizeMedium: {
          height: pxToRem(32)
        },
        textSizeLarge: {
          height: pxToRem(40),
          fontSize: pxToRem(14)
        },
        outlinedSizeMedium: {
          height: pxToRem(32)
        },
        outlinedSizeLarge: {
          height: pxToRem(40),
          fontSize: pxToRem(14)
        },
        containedPrimary: {
          background: NonMuiColors.SAGE_GREEN,
          color: NonMuiColors.WHITE,
          "&:hover": {
            background: NonMuiColors.IN_APP_HOVER
          },
          "&:focus-visible": {
            outline: "none",
            boxShadow: `0 0 0 ${pxToRem(1)} ${NonMuiColors.WHITE}, 0 0 0 ${pxToRem(3)} ${NonMuiColors.FOCUS_VISIBLE_COLOR}, 0 0 0 ${pxToRem(4)} ${NonMuiColors.WHITE}`
          },
          "&.Mui-disabled": {
            backgroundColor: grey[600],
            color: NonMuiColors.WHITE
          },
          "&:active": {
            background: grey[800],
            color: NonMuiColors.WHITE
          }
        },
        containedSecondary: {
          background: grey[100],
          color: NonMuiColors.SAGE_GREEN,
          "&:hover": {
            background: darken(grey[100], 0.08)
          },
          "&:focus-visible": {
            outline: "none",
            boxShadow: `0 0 0 ${pxToRem(1)} ${NonMuiColors.WHITE}, 0 0 0 ${pxToRem(3)} ${NonMuiColors.FOCUS_VISIBLE_COLOR}, 0 0 0 ${pxToRem(4)} ${NonMuiColors.WHITE}`
          },
          "&.Mui-disabled": {
            backgroundColor: grey[100],
            color: grey[600]
          },
          "&:active": {
            background: grey[200],
            color: grey[800]
          }
        },
        outlined: {
          border: `${pxToRem(1)} solid ${NonMuiColors.SAGE_GREEN}`,
          background: "transparent",
          color: NonMuiColors.SAGE_GREEN,
          "&:hover": {
            border: `${pxToRem(1)} solid ${green[900]}`,
            background: NonMuiColors.IN_APP_HOVER,
            color: NonMuiColors.WHITE
          },
          "&:focus-visible": {
            outline: "none",
            boxShadow: `0 0 0 ${pxToRem(1)} ${NonMuiColors.WHITE}, 0 0 0 ${pxToRem(3)} ${NonMuiColors.FOCUS_VISIBLE_COLOR}, 0 0 0 ${pxToRem(4)} ${NonMuiColors.WHITE}`
          },
          "&:active": {
            background: NonMuiColors.SAGE_GREEN,
            color: NonMuiColors.WHITE
          },
          "&.Mui-disabled": {
            color: grey[600],
            border: `${pxToRem(1)} solid ${grey[600]}`
          }
        },
        text: {
          color: grey[900],
          "&:hover": {
            background: grey[100]
          },
          "&:focus-visible": {
            outline: "none",
            boxShadow: `0 0 0 ${pxToRem(1)} ${NonMuiColors.WHITE}, 0 0 0 ${pxToRem(3)} ${NonMuiColors.FOCUS_VISIBLE_COLOR}, 0 0 0 ${pxToRem(4)} ${NonMuiColors.WHITE}`
          },
          "&:active": {
            background: grey[800],
            color: NonMuiColors.WHITE
          }
        }
      }
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          padding: pxToRem(8),
          borderRadius: pxToRem(8),
          "&:hover": {
            backgroundColor: grey[100]
          },
          "&:active": {
            backgroundColor: grey[400]
          },
          "&:focus-visible": {
            outline: "none",
            boxShadow: `0 0 0 ${pxToRem(1)} ${NonMuiColors.WHITE}, 0 0 0 ${pxToRem(3)} ${NonMuiColors.FOCUS_VISIBLE_COLOR}, 0 0 0 ${pxToRem(4)} ${NonMuiColors.WHITE}`
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            color: grey[800],
            border: `${pxToRem(1)} solid ${grey[200]}`,
            borderRadius: pxToRem(8),
            padding: `${pxToRem(10)} ${pxToRem(12)}`,
            fontSize: pxToRem(14),
            fontFamily: fontFamilyConstants.SAGE_UI_ADAPTIVE,
            fontWeight: 400,
            transition: "border-color 150ms linear",
            height: pxToRem(40),
            "& > .MuiInputBase-input": {
              padding: 0,
              paddingRight: pxToRem(20)
            },
            "&:hover": {
              border: `${pxToRem(1)} solid ${grey[600]}`,
              boxShadow: `0 ${pxToRem(6)} ${pxToRem(12)} ${NonMuiColors.BOX_SHADOW_COLOR_DARKER_SHADE}`
            },
            "&.Mui-error": {
              border: `${pxToRem(1)} solid ${red[900]}`
            },
            "&.Mui-focused": {
              outline: "none",
              boxShadow: `0 0 0 ${pxToRem(1)} ${NonMuiColors.WHITE}, 0 0 0 ${pxToRem(3)} ${NonMuiColors.FOCUS_VISIBLE_COLOR}, 0 0 0 ${pxToRem(4)} ${NonMuiColors.WHITE}`
            },
            "&.Mui-disabled": {
              backgroundColor: grey[100],
              border: `${pxToRem(1)} solid ${grey[200]}`,
              color: grey[500]
            }
          },
          "& > .MuiFormHelperText-root": {
            marginInline: 0,
            marginTop: pxToRem(8),
            "&.Mui-error": {
              color: "#B91C1C",
              fontSize: pxToRem(14),
              fontWeight: 400,
              lineHeight: "150%"
            }
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: pxToRem(8)
        },
        outlined: {
          border: `${pxToRem(1)} solid ${grey[200]}`
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: grey[800],
          fontWeight: 400,
          fontSize: pxToRem(14),
          lineHeight: pxToRem(21),
          borderRadius: pxToRem(8),
          padding: `${pxToRem(8)} ${pxToRem(12)}`,
          "&:hover": {
            backgroundColor: grey[100]
          },
          "&.Mui-selected": {
            backgroundColor: grey[100],
            "&:hover": {
              backgroundColor: grey[100]
            }
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          border: 0
        },
        root: {
          border: `${pxToRem(1)} solid ${grey[200]}`,
          "&:hover": {
            border: `${pxToRem(1)} solid ${grey[800]}`,
            boxShadow: `0 ${pxToRem(6)} ${pxToRem(12)} rgba(0, 0, 0, 0.15)`
          },
          "&.Mui-focused": {
            outline: "none",
            boxShadow: `0 0 0 ${pxToRem(1)} ${NonMuiColors.WHITE}, 0 0 0 ${pxToRem(3)} ${NonMuiColors.FOCUS_VISIBLE_COLOR}, 0 0 0 ${pxToRem(4)} ${NonMuiColors.WHITE}`
          },
          "&.Mui-error": {
            border: `${pxToRem(1)} solid ${red[900]}`
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: pxToRem(16),
          fontWeight: 500,
          fontSize: pxToRem(12)
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: fontFamilyConstants.SAGE_UI_ADAPTIVE,
          fontSize: pxToRem(14),
          borderBottom: `${pxToRem(1)} solid ${grey[200]}`
        },
        head: {
          fontWeight: 600,
          color: grey[700],
          backgroundColor: grey[50]
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: grey[50]
          }
        }
      }
    }
  }
};
